import { supabase } from '../supabaseClient';

/**
 * Every capability the portal can expose. The same key names the agency's
 * per-contract switch and the per-user grant, and the server grants access only
 * when both agree — see `client_portal_can` in the migration.
 */
export type ClientPortalScope =
  | 'live_map'
  | 'active_guards'
  | 'patrol_progress'
  | 'incidents'
  | 'daily_reports'
  | 'patrol_records'
  | 'visitor_logs'
  | 'post_orders'
  | 'schedule'
  | 'service_requests'
  | 'exports'
  | 'team';

export const CLIENT_PORTAL_SCOPES: ClientPortalScope[] = [
  'live_map',
  'active_guards',
  'patrol_progress',
  'incidents',
  'daily_reports',
  'patrol_records',
  'visitor_logs',
  'post_orders',
  'schedule',
  'service_requests',
  'exports',
  'team',
];

export type ReportReleaseMode = 'immediate' | 'on_approval';
export type GuardContactVisibility = 'full' | 'name_only' | 'hidden';

export interface ClientPortalSettings {
  client_contract_id: string;
  live_tracking_enabled: boolean;
  active_guards_enabled: boolean;
  patrol_progress_enabled: boolean;
  incident_reports_enabled: boolean;
  daily_reports_enabled: boolean;
  patrol_records_enabled: boolean;
  visitor_logs_enabled: boolean;
  exports_enabled: boolean;
  post_orders_enabled: boolean;
  schedule_enabled: boolean;
  service_requests_enabled: boolean;
  sub_accounts_enabled: boolean;
  incident_release_mode: ReportReleaseMode;
  daily_report_release_mode: ReportReleaseMode;
  guard_contact_visibility: GuardContactVisibility;
  report_history_days: number | null;
}

export interface ClientPortalSite {
  id: string;
  name: string;
  address: string | null;
  timezone: string | null;
  required_coverage_start_minute: number | null;
  required_coverage_end_minute: number | null;
}

export interface ClientPortalContext {
  user: {
    id: string;
    email: string;
    full_name: string | null;
    job_title: string | null;
    role: 'primary' | 'member';
    site_scope: string[] | null;
  };
  contract: {
    id: string;
    name: string;
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    status: string;
  };
  company: { id: string; name: string };
  settings: ClientPortalSettings;
  scopes: ClientPortalScope[];
  sites: ClientPortalSite[];
}

export interface ClientPortalActiveGuard {
  shiftExecutionId: string;
  siteId: string;
  siteName: string;
  guardName: string | null;
  guardPhone: string | null;
  guardAvatarUrl: string | null;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
  actualClockInAt: string | null;
  currentSegmentLabel: string | null;
  currentSegmentType: string | null;
  lastLocationAt: string | null;
}

export interface ClientPortalLivePosition {
  shiftExecutionId: string;
  siteId: string;
  siteName: string;
  guardName: string | null;
  latitude: number;
  longitude: number;
  heading: number | null;
  accuracy: number | null;
  recordedAt: string;
  updatedAt: string;
}

export type PatrolSweepStatus = 'pending' | 'in_progress' | 'completed' | 'missed' | 'skipped';

export interface ClientPortalPatrolSweep {
  segmentId: string;
  shiftExecutionId: string;
  siteId: string;
  siteName: string;
  patrolId: string | null;
  patrolName: string | null;
  guardName: string | null;
  status: PatrolSweepStatus;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartedAt: string | null;
  actualEndedAt: string | null;
  startedLateBySeconds: number;
  checkpointsTotal: number;
  checkpointsCompleted: number;
  checkpointsMissed: number;
}

export interface ClientPortalCheckpointHit {
  checkpointId: string | null;
  checkpointName: string | null;
  checkpointCode: string | null;
  plannedSequenceOrder: number | null;
  status: 'completed' | 'missed' | 'skipped';
  recordedAt: string;
  completedLateBySeconds: number;
  withinGeofence: boolean | null;
  notes: string | null;
}

export interface ClientPortalCoverageSlot {
  dutySlotId: string;
  siteId: string;
  siteName: string;
  slotDate: string;
  name: string;
  dutyKind: string;
  startMinute: number;
  endMinute: number;
  guardsRequired: number;
  guardsAssigned: number;
}

export interface ClientPortalReport {
  id: string;
  site_id: string | null;
  created_at: string;
  title: string | null;
  content: string | null;
  report_type: string | null;
  report_data: Record<string, unknown> | null;
  priority: string | null;
  status: string;
  client_released_at: string | null;
  sites?: { name: string | null } | null;
  [key: string]: unknown;
}

export interface ClientPortalPostOrder {
  id: string;
  site_id: string;
  category: string;
  title: string;
  body: string;
  display_order: number;
  version: number;
  effective_from: string | null;
  updated_at: string;
}

export type ServiceRequestType =
  | 'extra_hours'
  | 'emergency_dispatch'
  | 'event_coverage'
  | 'site_visit'
  | 'other';
export type ServiceRequestUrgency = 'routine' | 'urgent' | 'emergency';
export type ServiceRequestStatus =
  | 'submitted'
  | 'acknowledged'
  | 'scheduled'
  | 'declined'
  | 'cancelled'
  | 'completed';

export interface ClientServiceRequest {
  id: string;
  site_id: string | null;
  request_type: ServiceRequestType;
  urgency: ServiceRequestUrgency;
  status: ServiceRequestStatus;
  title: string;
  details: string | null;
  requested_start_at: string | null;
  requested_end_at: string | null;
  guards_required: number;
  response_note: string | null;
  handled_at: string | null;
  created_at: string;
}

export interface CreateServiceRequestParams {
  title: string;
  requestType: ServiceRequestType;
  urgency: ServiceRequestUrgency;
  siteId?: string | null;
  details?: string | null;
  requestedStartAt?: string | null;
  requestedEndAt?: string | null;
  guardsRequired?: number;
}

export interface ClientPortalTeamMember {
  id: string | null;
  email: string;
  full_name: string | null;
  job_title: string | null;
  role: 'primary' | 'member';
  status: 'active' | 'disabled' | 'invited';
  permissions: ClientPortalScope[];
  site_scope: string[] | null;
  last_login_at: string | null;
  created_at: string;
  invite_id: string | null;
  invite_status: string | null;
  invite_expires_at: string | null;
}

export interface InviteMemberParams {
  email: string;
  fullName?: string | null;
  jobTitle?: string | null;
  permissions: ClientPortalScope[];
  siteScope?: string[] | null;
}

export interface InviteMemberResult {
  inviteId: string;
  email: string;
  expiresAt: string;
  /** Present so the primary contact can pass the link on if email never lands. */
  inviteToken: string;
}

/** Raw shapes as the RPCs return them — snake_case, mapped below. */
interface ActiveGuardRow {
  shift_execution_id: string;
  site_id: string;
  site_name: string;
  guard_name: string | null;
  guard_phone: string | null;
  guard_avatar_url: string | null;
  planned_start_at: string | null;
  planned_end_at: string | null;
  actual_clock_in_at: string | null;
  current_segment_label: string | null;
  current_segment_type: string | null;
  last_location_at: string | null;
}

interface LivePositionRow {
  shift_execution_id: string;
  site_id: string;
  site_name: string;
  guard_name: string | null;
  latitude: number | null;
  longitude: number | null;
  heading: number | null;
  accuracy: number | null;
  recorded_at: string;
  updated_at: string;
}

interface PatrolSweepRow {
  segment_id: string;
  shift_execution_id: string;
  site_id: string;
  site_name: string;
  patrol_id: string | null;
  patrol_name: string | null;
  guard_name: string | null;
  status: string;
  planned_start_at: string;
  planned_end_at: string;
  actual_started_at: string | null;
  actual_ended_at: string | null;
  started_late_by_seconds: number | null;
  checkpoints_total: number | null;
  checkpoints_completed: number | null;
  checkpoints_missed: number | null;
}

interface CheckpointHitRow {
  checkpoint_id: string | null;
  checkpoint_name: string | null;
  checkpoint_code: string | null;
  planned_sequence_order: number | null;
  status: string;
  recorded_at: string;
  completed_late_by_seconds: number | null;
  within_geofence: boolean | null;
  notes: string | null;
}

interface CoverageRow {
  duty_slot_id: string;
  site_id: string;
  site_name: string;
  slot_date: string;
  name: string;
  duty_kind: string;
  start_minute: number;
  end_minute: number;
  guards_required: number;
  guards_assigned: number;
}

interface TeamRow {
  id: string | null;
  email: string;
  full_name: string | null;
  job_title: string | null;
  role: string;
  status: string;
  permissions: unknown;
  site_scope: string[] | null;
  last_login_at: string | null;
  created_at: string;
  invite_id: string | null;
  invite_status: string | null;
  invite_expires_at: string | null;
}

const REPORT_SELECT = `
  id, site_id, created_at, title, content, report_type, report_data,
  priority, status, client_released_at, guard_id, company_id,
  shift_execution_id, shift_execution_segment_id, is_auto_generated,
  status_updated_at, status_reason, assigned_to,
  sites:sites(name)
`;

function toActiveGuard(row: ActiveGuardRow): ClientPortalActiveGuard {
  return {
    shiftExecutionId: row.shift_execution_id,
    siteId: row.site_id,
    siteName: row.site_name,
    guardName: row.guard_name,
    guardPhone: row.guard_phone,
    guardAvatarUrl: row.guard_avatar_url,
    plannedStartAt: row.planned_start_at,
    plannedEndAt: row.planned_end_at,
    actualClockInAt: row.actual_clock_in_at,
    currentSegmentLabel: row.current_segment_label,
    currentSegmentType: row.current_segment_type,
    lastLocationAt: row.last_location_at,
  };
}

function toPatrolSweep(row: PatrolSweepRow): ClientPortalPatrolSweep {
  return {
    segmentId: row.segment_id,
    shiftExecutionId: row.shift_execution_id,
    siteId: row.site_id,
    siteName: row.site_name,
    patrolId: row.patrol_id,
    patrolName: row.patrol_name,
    guardName: row.guard_name,
    status: (row.status as PatrolSweepStatus) ?? 'pending',
    plannedStartAt: row.planned_start_at,
    plannedEndAt: row.planned_end_at,
    actualStartedAt: row.actual_started_at,
    actualEndedAt: row.actual_ended_at,
    startedLateBySeconds: row.started_late_by_seconds ?? 0,
    checkpointsTotal: row.checkpoints_total ?? 0,
    checkpointsCompleted: row.checkpoints_completed ?? 0,
    checkpointsMissed: row.checkpoints_missed ?? 0,
  };
}

function toTeamMember(row: TeamRow): ClientPortalTeamMember {
  return {
    id: row.id,
    email: row.email,
    full_name: row.full_name,
    job_title: row.job_title,
    role: row.role === 'primary' ? 'primary' : 'member',
    status: row.status as ClientPortalTeamMember['status'],
    permissions: Array.isArray(row.permissions) ? (row.permissions as ClientPortalScope[]) : [],
    site_scope: row.site_scope,
    last_login_at: row.last_login_at,
    created_at: row.created_at,
    invite_id: row.invite_id,
    invite_status: row.invite_status,
    invite_expires_at: row.invite_expires_at,
  };
}

export const clientPortalService = {
  /** One call bootstraps the shell: identity, sites, and what this user may see. */
  async fetchContext(): Promise<ClientPortalContext> {
    const { data, error } = await supabase.rpc('client_portal_context');
    if (error) throw error;
    return data as ClientPortalContext;
  },

  async fetchActiveGuards(): Promise<ClientPortalActiveGuard[]> {
    const { data, error } = await supabase.rpc('client_portal_active_guards');
    if (error) throw error;
    return ((data ?? []) as ActiveGuardRow[]).map(toActiveGuard);
  },

  async fetchLivePositions(): Promise<ClientPortalLivePosition[]> {
    const { data, error } = await supabase.rpc('client_portal_live_positions');
    if (error) throw error;
    return ((data ?? []) as LivePositionRow[])
      .filter((row) => row.latitude !== null && row.longitude !== null)
      .map((row) => ({
        shiftExecutionId: row.shift_execution_id,
        siteId: row.site_id,
        siteName: row.site_name,
        guardName: row.guard_name,
        latitude: row.latitude as number,
        longitude: row.longitude as number,
        heading: row.heading,
        accuracy: row.accuracy,
        recordedAt: row.recorded_at,
        updatedAt: row.updated_at,
      }));
  },

  async fetchPatrolProgress(
    from: Date,
    to: Date,
    siteId?: string | null,
  ): Promise<ClientPortalPatrolSweep[]> {
    const { data, error } = await supabase.rpc('client_portal_patrol_progress', {
      p_from: from.toISOString(),
      p_to: to.toISOString(),
      p_site_id: siteId ?? null,
    });
    if (error) throw error;
    return ((data ?? []) as PatrolSweepRow[]).map(toPatrolSweep);
  },

  async fetchPatrolCheckpoints(segmentId: string): Promise<ClientPortalCheckpointHit[]> {
    const { data, error } = await supabase.rpc('client_portal_patrol_checkpoints', {
      p_segment_id: segmentId,
    });
    if (error) throw error;
    return ((data ?? []) as CheckpointHitRow[]).map((row) => ({
      checkpointId: row.checkpoint_id,
      checkpointName: row.checkpoint_name,
      checkpointCode: row.checkpoint_code,
      plannedSequenceOrder: row.planned_sequence_order,
      status: (row.status as ClientPortalCheckpointHit['status']) ?? 'completed',
      recordedAt: row.recorded_at,
      completedLateBySeconds: row.completed_late_by_seconds ?? 0,
      withinGeofence: row.within_geofence,
      notes: row.notes,
    }));
  },

  async fetchCoverage(
    from: string,
    to: string,
    siteId?: string | null,
  ): Promise<ClientPortalCoverageSlot[]> {
    const { data, error } = await supabase.rpc('client_portal_coverage', {
      p_from: from,
      p_to: to,
      p_site_id: siteId ?? null,
    });
    if (error) throw error;
    return ((data ?? []) as CoverageRow[]).map((row) => ({
      dutySlotId: row.duty_slot_id,
      siteId: row.site_id,
      siteName: row.site_name,
      slotDate: row.slot_date,
      name: row.name,
      dutyKind: row.duty_kind,
      startMinute: row.start_minute,
      endMinute: row.end_minute,
      guardsRequired: row.guards_required,
      guardsAssigned: row.guards_assigned,
    }));
  },

  /**
   * Report types are passed through verbatim; RLS decides what comes back, so an
   * unreleased or out-of-scope report is simply absent rather than an error.
   */
  async fetchReports(params: {
    reportTypes?: string[];
    siteId?: string | null;
    search?: string;
    fromDate?: string | null;
    toDate?: string | null;
    limit?: number;
  } = {}): Promise<ClientPortalReport[]> {
    let query = supabase
      .from('reports')
      .select(REPORT_SELECT)
      .order('created_at', { ascending: false })
      .limit(params.limit ?? 200);

    if (params.reportTypes?.length) query = query.in('report_type', params.reportTypes);
    if (params.siteId) query = query.eq('site_id', params.siteId);
    if (params.fromDate) query = query.gte('created_at', params.fromDate);
    if (params.toDate) query = query.lte('created_at', params.toDate);
    if (params.search?.trim()) {
      const term = `%${params.search.trim()}%`;
      query = query.or(`title.ilike.${term},content.ilike.${term}`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as ClientPortalReport[];
  },

  async fetchReportById(reportId: string): Promise<ClientPortalReport> {
    const { data, error } = await supabase
      .from('reports')
      .select(REPORT_SELECT)
      .eq('id', reportId)
      .single();
    if (error) throw error;
    return data as unknown as ClientPortalReport;
  },

  async fetchReportMedia(reportId: string): Promise<Array<{ id: string; storage_path: string; media_type: string; url: string | null }>> {
    const { data, error } = await supabase
      .from('reports_media')
      .select('id, storage_path, media_type')
      .eq('report_id', reportId);
    if (error) throw error;

    const rows = (data ?? []) as Array<{ id: string; storage_path: string; media_type: string }>;
    return await Promise.all(rows.map(async (row) => {
      const { data: signed } = await supabase.storage
        .from('reports')
        .createSignedUrl(row.storage_path, 60 * 60);
      return { ...row, url: signed?.signedUrl ?? null };
    }));
  },

  async fetchPostOrders(siteId?: string | null): Promise<ClientPortalPostOrder[]> {
    let query = supabase
      .from('site_post_orders')
      .select('id, site_id, category, title, body, display_order, version, effective_from, updated_at')
      .order('display_order', { ascending: true })
      .order('title', { ascending: true });

    if (siteId) query = query.eq('site_id', siteId);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as ClientPortalPostOrder[];
  },

  async fetchServiceRequests(): Promise<ClientServiceRequest[]> {
    const { data, error } = await supabase
      .from('client_service_requests')
      .select('id, site_id, request_type, urgency, status, title, details, requested_start_at, requested_end_at, guards_required, response_note, handled_at, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ClientServiceRequest[];
  },

  async createServiceRequest(params: CreateServiceRequestParams): Promise<string> {
    const { data, error } = await supabase.rpc('client_portal_create_service_request', {
      p_title: params.title,
      p_request_type: params.requestType,
      p_urgency: params.urgency,
      p_site_id: params.siteId ?? null,
      p_details: params.details ?? null,
      p_requested_start_at: params.requestedStartAt ?? null,
      p_requested_end_at: params.requestedEndAt ?? null,
      p_guards_required: params.guardsRequired ?? 1,
    });
    if (error) throw error;
    return (data as { request_id: string }).request_id;
  },

  async cancelServiceRequest(requestId: string): Promise<void> {
    const { error } = await supabase
      .from('client_service_requests')
      .update({ status: 'cancelled' })
      .eq('id', requestId);
    if (error) throw error;
  },

  async fetchTeam(): Promise<ClientPortalTeamMember[]> {
    const { data, error } = await supabase.rpc('client_portal_team');
    if (error) throw error;
    return ((data ?? []) as TeamRow[]).map(toTeamMember);
  },

  /**
   * The edge function creates the invite and emails it in one step. If it is
   * unreachable we still create the invite directly, so the primary contact can
   * fall back to copying the link the UI shows.
   */
  async inviteMember(params: InviteMemberParams): Promise<InviteMemberResult> {
    const { data, error } = await supabase.functions.invoke('send-client-invite', {
      body: {
        action: 'member-create',
        email: params.email,
        fullName: params.fullName ?? null,
        jobTitle: params.jobTitle ?? null,
        permissions: params.permissions,
        siteScope: params.siteScope ?? null,
      },
    });

    if (!error && data) {
      const sent = data as {
        inviteId: string;
        email: string;
        expiresAt: string;
        inviteToken: string;
      };
      return {
        inviteId: sent.inviteId,
        email: sent.email,
        expiresAt: sent.expiresAt,
        inviteToken: sent.inviteToken,
      };
    }

    const { data: rpcData, error: rpcError } = await supabase.rpc(
      'client_portal_create_member_invite',
      {
        p_email: params.email,
        p_full_name: params.fullName ?? null,
        p_job_title: params.jobTitle ?? null,
        p_permissions: params.permissions,
        p_site_scope: params.siteScope ?? null,
      },
    );
    if (rpcError) throw rpcError;

    const result = rpcData as {
      invite_id: string;
      email: string;
      expires_at: string;
      invite_token: string;
    };

    return {
      inviteId: result.invite_id,
      email: result.email,
      expiresAt: result.expires_at,
      inviteToken: result.invite_token,
    };
  },

  async updateMember(params: {
    userId: string;
    permissions?: ClientPortalScope[];
    siteScope?: string[] | null;
    clearSiteScope?: boolean;
    jobTitle?: string | null;
    status?: 'active' | 'disabled';
  }): Promise<void> {
    const { error } = await supabase.rpc('client_portal_update_member', {
      p_user_id: params.userId,
      p_permissions: params.permissions ?? null,
      p_site_scope: params.siteScope ?? null,
      p_clear_site_scope: params.clearSiteScope ?? false,
      p_job_title: params.jobTitle ?? null,
      p_status: params.status ?? null,
    });
    if (error) throw error;
  },

  async cancelMemberInvite(inviteId: string): Promise<void> {
    const { error } = await supabase.rpc('client_portal_cancel_member_invite', {
      p_invite_id: inviteId,
    });
    if (error) throw error;
  },

  /**
   * Live positions arrive through the same table the workspace map uses; RLS
   * limits the stream to officers on this client's sites.
   */
  subscribeToLivePositions(onChange: () => void): () => void {
    const channel = supabase
      .channel('client_portal_live_locations')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'guard_live_locations' },
        () => onChange(),
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
